import {
  getAvailabilityBoundriesForDateForOwner,
  getIsUnavailableAtTime,
  getIsUnavailableByDateAvailability,
  getIsUnavailableByDayOfWeekAvailability,
  isBoundsWithinBoundsList,
} from 'Common/functions/Availability';
import dayjs from 'dayjs';
import {
  ServiceProviderDetailsDateAvailability,
  ServiceProviderDetailsDayOfWeekAvailability,
} from '../ServiceProviderDetails';
import { divideIntoTimeSlots } from 'Common/functions/BookingForm';
import { UnavailableDatetime } from 'Generated/graphql';

export const getDateTimeIsDisabledForServiceProvider = (
  start: number,
  end: number,
  spDateAvailability: ServiceProviderDetailsDateAvailability,
  spDayOfWeekAvailability: ServiceProviderDetailsDayOfWeekAvailability,
  unavailableTimeBounds: {
    startDateTime: number;
    endDateTime: number;
  }[],
  ignoreIsPast: boolean,
  skipConflictingEventCheck: boolean,
  isDebug?: boolean,
) => {
  const startDayjs = dayjs.unix(start);
  const printDebug = (...msg: unknown[]) => {
    // eslint-disable-next-line no-console
    /*
    console.log(
      start,
      startDayjs.format('YYYY-MM-DD h:mm A'),
      'is disabled because:',
      ...msg,
    );
    */
  };

  if (!spDateAvailability ||
    !spDayOfWeekAvailability) {
    isDebug && printDebug(
      'no date availability or no day of week availability was given',
      spDateAvailability,
      spDayOfWeekAvailability,
    );
    return true;
  }

  if (!ignoreIsPast) {
    const now = dayjs();
    const isPast = startDayjs.isBefore(now);

    if (isPast) {
      isDebug && printDebug(
        `it is past time: ${now.format('YYYY-MM-DD h:mm A')}`,
      );
      return true;
    }
  }

  if (!skipConflictingEventCheck) {
    const isUnavailableByConflictingEvent = getIsUnavailableAtTime(
      start,
      end,
      unavailableTimeBounds.map(b => ({
        min: b.startDateTime,
        max: b.endDateTime,
      })),
    );
    if (isUnavailableByConflictingEvent) {
      isDebug && printDebug(
        'it is unavailable by conflicting event',
        unavailableTimeBounds,
      );
      return true;
    }
  }

  const isUnavailableByDateAvailability =
    getIsUnavailableByDateAvailability(
      start,
      end,
      spDateAvailability,
    );

  if (isUnavailableByDateAvailability) {
    isDebug && printDebug(
      'it is unavailable by date availability',
      spDateAvailability,
    );
    return true;
  } else if (isUnavailableByDateAvailability === false) {
    return false;
  } else if (isUnavailableByDateAvailability === undefined) {
    const isUnavailableByDayOfWeekAvailability =
      getIsUnavailableByDayOfWeekAvailability(
        start,
        end,
        spDayOfWeekAvailability,
      );

    if (isUnavailableByDayOfWeekAvailability) {
      isDebug && printDebug(
        'it is unavailable by day of week availability',
        spDayOfWeekAvailability,
      );
      return true;
    }
  }

  return false;
};

export const filterServiceProviderTimeSlotsByDuration = (
  allSlots: number[],
  totalDurationSeconds: number,
  spAvailabilityBoundries: [number, number][],
) => {
  const boundries = spAvailabilityBoundries.map(b => ({
    min: b[0],
    max: b[1],
  }));

  const filteredByDuration = allSlots.filter(
    ts => isBoundsWithinBoundsList({
      min: ts,
      max: ts + totalDurationSeconds,
    }, boundries, true)
  );

  return filteredByDuration;
};

export const getAvailableTimeSlotsInDateTimeRange = (
  serviceProviderOwnerUid: string,
  spDayOfWeekAvailability: ServiceProviderDetailsDayOfWeekAvailability,
  spDateAvailability: ServiceProviderDetailsDateAvailability,
  unavailableTimeBounds: UnavailableDatetime[],
  from: number,
  to: number,
  durationSeconds: number,
  ignoreIsPast: boolean,
) => {
  const boundries =
    getAvailabilityBoundriesForDateForOwner(
      from,
      serviceProviderOwnerUid,
      spDayOfWeekAvailability,
      spDateAvailability,
    );

  const allSlots = boundries.filter(
    b => b[0] >= from && b[1] <= to
  ).flatMap(
    b => divideIntoTimeSlots(
      dayjs.unix(b[0]),
      dayjs.unix(b[1]),
      30,
    )
  );

  const ts = allSlots.filter(
    (ts) => {
      const isUnavailable = getDateTimeIsDisabledForServiceProvider(
        ts,
        ts + durationSeconds,
        spDateAvailability,
        spDayOfWeekAvailability,
        unavailableTimeBounds,
        ignoreIsPast,
        false,
        false,
      );

      return !isUnavailable;
    }
  );

  const slots = filterServiceProviderTimeSlotsByDuration(
    ts,
    durationSeconds,
    boundries,
  );

  return slots;
};

export const getUnixDateSlots = (
  startDate: number,
  count: number,
) => {
  const startDateFixed = Math.abs(Math.round(startDate));
  const countFixed = Math.abs(Math.round(count));
  const startOfStartDate = dayjs.unix(startDateFixed).startOf('d');

  return ([...new Array(countFixed)]).map(
    (unused, i) => {
      return startOfStartDate.clone().add(i, 'd').unix();
    }
  ).sort((a, b) => a - b);
};

export const getServiceProviderHasOpenSlotsForDateFn = (
  getAllowNewBookingWithinAvailabilityBuffer: (d: number) => boolean,
  getTimeSlots: (from: number, to: number) => number[],
) => (
  dateSlot: number,
  nowDayjs: dayjs.Dayjs,
) => {
  const dateSlotDayjs = dayjs.unix(dateSlot);
  const endOfAvailabilityBuffer = dayjs().add(1, 'd').endOf('d');
  const isWithinAvailabilityBuffer =
    nowDayjs.isSame(dateSlotDayjs, 'd') || dateSlotDayjs.isBefore(endOfAvailabilityBuffer);

  const allowBookingWithinBuffer =
    getAllowNewBookingWithinAvailabilityBuffer(nowDayjs.unix());
  if (isWithinAvailabilityBuffer && !allowBookingWithinBuffer) {
    return false;
  }

  const dateSlotUnix = dayjs.unix(dateSlot);
  const startOfDate = dateSlotUnix.clone().startOf('d').unix();
  const endOfDate = dateSlotUnix.clone().endOf('d').unix();

  const timeSlotsForDate = getTimeSlots(startOfDate, endOfDate);
  const hasTimeSlotsForDate = timeSlotsForDate.length > 0;

  return hasTimeSlotsForDate;
};

export const getServiceProviderAvailableDates = (
  nowSec: number,
  dateSlots: number[],
  getHasOpenTimeSlotsForDate: (d: number, nowDayjs: dayjs.Dayjs) => boolean,
) => {
  const nowDayjs = dayjs.unix(nowSec);

  const filteredDateSlots = dateSlots.filter(
    dateSlot => getHasOpenTimeSlotsForDate(
      dateSlot,
      nowDayjs,
    ),
  );

  return filteredDateSlots;
};

export const getDateSlotsWithAvailabilityForUpcomingMonth = (
  checkFromDate: number,
  getServiceProviderHasOpenSlotsForDate: (
    dateSlot: number,
    nowDayjs: dayjs.Dayjs,
  ) => boolean,
) => {
  const dateSlots = getUnixDateSlots(checkFromDate, 31);

  return getServiceProviderAvailableDates(
    dayjs().unix(),
    dateSlots,
    getServiceProviderHasOpenSlotsForDate,
  );
};

export const serviceProviderAvailavilityHandleRecommendedStartDateTime = (
  timeUnix: number | undefined,
  recommendedStartDateTime: number | undefined,
  timeSlots: number[],
  onFoundFirstTimeSlotAtOrAfterRecommendedStartTime: (v: number) => void,
) => {
  if (timeUnix) {
    return;
  }

  if (recommendedStartDateTime && timeSlots.length > 0) {
    const firstTimeSlotAfterRecommendedStartTime = timeSlots
      .sort((a, b) => a - b)
      .find(s => s >= recommendedStartDateTime);

    if (firstTimeSlotAfterRecommendedStartTime) {
      onFoundFirstTimeSlotAtOrAfterRecommendedStartTime(
        firstTimeSlotAfterRecommendedStartTime,
      );
    }
  }
};
